import { Pencil } from 'lucide-react';
import type { CardDetails } from '@/types/reveal';
import { useDayFixtures, findFixtureForCard, formatFixtureStatus } from '@/hooks/useDailyReveal';
import { resolveTeamAbbreviation, toTeamAbbreviation } from '@/utils/teamAbbreviation';
import { getPpdCreditLabel } from '@/utils/ppdCreditLabel';
import { resolveMlbStylePlayerImage } from '@/types/reveal';
import { PlayerPickAvatar } from './PlayerPickAvatar';
import PropStatProgressTracker from './PropStatProgressTracker';
import PropOutcomeIcon from './PropOutcomeIcon';
import {
  formatPropResultLabel,
  getPropTrackerBorderClass,
  getPropTrackerDisplayParts,
  resolvePropTrackerOutcome,
  resolvePropTrackerThreshold,
  resolvePropTrackerOperator,
} from '@/utils/propBasedDailyReveal';

export type WeeklySelectionCardProps = {
  dayId: string;
  card: CardDetails;
  dateLabel: string;
  totalHits: number;
  isGraded: boolean;
  won: boolean;
  /** Hit was auto-credited because the pick's game was postponed/cancelled (DKPLAT-478). */
  ppdCredited?: boolean;
  /** Why the credit was granted ('postponed' | 'cancelled'), persisted at grading time. */
  ppdStatus?: string | null;
  dayStatus: string;
  leagueIpEnabled?: boolean;
  isPropBased?: boolean;
  categoryName?: string | null;
  propDisplayLabel?: string | null;
  propResultStatus?: string | null;
  propResultValue?: number | null;
  propThreshold?: number | null;
  propOperator?: string | null;
  /**
   * Navigate to this day's reveal so the player can change the pick. Supplied by
   * `WeeklySelectionsSection` only while the day's `pickCutoffAt` is still ahead —
   * this card does no gating of its own, and deliberately does NOT look at
   * `dayStatus` (see `canEditDaySelection`).
   */
  onEdit?: () => void;
};

function formatAbbreviatedName(firstName: string, lastName: string, displayName: string): string {
  const first = (firstName || displayName.split(/\s+/)[0] || '').trim();
  const last = (lastName || displayName.split(/\s+/).slice(1).join(' ') || '').trim();
  if (first && last) return `${first.charAt(0)}. ${last}`.toUpperCase();
  return (displayName || `${first} ${last}`.trim()).toUpperCase();
}

export default function WeeklySelectionCard({
  dayId,
  card,
  dateLabel,
  totalHits,
  isGraded,
  won,
  ppdCredited = false,
  ppdStatus = null,
  dayStatus,
  leagueIpEnabled = false,
  isPropBased = false,
  categoryName,
  propDisplayLabel,
  propResultStatus,
  propResultValue,
  propThreshold,
  propOperator,
  onEdit,
}: WeeklySelectionCardProps) {
  const { data: fixturesData } = useDayFixtures(dayId, {
    refetchInterval: query => {
      if (dayStatus === 'open' || dayStatus === 'locked') return 30_000;
      const fixtures = (query.state.data as any)?.fixtures ?? [];
      const hasLive = fixtures.some((f: any) => f.status === 'live' || f.status === 'in_progress');
      return hasLive ? 30_000 : false;
    },
  });

  const fixtures = fixturesData?.fixtures ?? [];
  const teamCode = resolveTeamAbbreviation(card.teamAbbreviation, card.teamName);
  const fixture = findFixtureForCard(card.teamExternalId, fixtures, teamCode);

  const awayTeamRaw = fixture?.awayTeam ?? '';
  const homeTeamRaw = fixture?.homeTeam ?? card.teamAbbreviation ?? card.teamName ?? '';
  const awayTeam = awayTeamRaw ? toTeamAbbreviation(awayTeamRaw) : '';
  const homeTeam = homeTeamRaw ? toTeamAbbreviation(homeTeamRaw) : '';
  const gameStatus = fixture ? formatFixtureStatus(fixture) : '';
  const isLive = fixture?.status === 'live';
  const isFinishedNoHits = !isGraded && totalHits === 0 && fixture?.status === 'finished';

  const threshold = resolvePropTrackerThreshold({ propThreshold, propDisplayLabel });
  const operator = resolvePropTrackerOperator({ propOperator, propDisplayLabel });
  const isLiveOrFinished = fixture?.status === 'live' || fixture?.status === 'finished' || isGraded;
  const trackerOutcome = resolvePropTrackerOutcome({
    propResultStatus,
    propResultValue,
    threshold,
    operator,
    totalHits,
    isGraded,
    isLiveOrFinished,
  });
  const showPropTracker =
    isPropBased &&
    threshold != null &&
    threshold > 0 &&
    (isLiveOrFinished || propResultValue != null || isGraded);

  const hasLiveHits = !isGraded && isLive && totalHits > 0;

  const borderColor = isPropBased
    ? getPropTrackerBorderClass(trackerOutcome)
    : isGraded
      ? won
        ? 'border-2 border-[#53D337]'
        : 'border-2 border-[#E9344A]'
      : hasLiveHits
        ? 'border-2 border-[#53D337]'
        : 'border-2 border-[#3a3a3a]';

  const isPpdHit = isGraded && ppdCredited;

  const propResult = isPropBased
    ? formatPropResultLabel(
        isGraded ? propResultStatus : 'pending',
        propResultValue,
        propDisplayLabel,
        propDisplayLabel ?? 'Pending'
      )
    : null;

  const hitLabel = isPropBased
    ? (propResult?.label ?? 'Pending')
    : isPpdHit
      ? getPpdCreditLabel(ppdStatus, fixture?.status)
      : isGraded
        ? totalHits > 0
          ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
          : '0 HITS'
        : isFinishedNoHits
          ? '0 HITS'
          : totalHits > 0
            ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
            : 'Pending';

  const hitColor = isPropBased
    ? isGraded
      ? propResult?.won
        ? 'text-[#53d337]'
        : propResultStatus === 'void'
          ? 'text-[#8a8a8a]'
          : 'text-[#EA2054]'
      : 'text-[#8a8a8a]'
    : isGraded
      ? won
        ? 'text-[#53d337]'
        : 'text-[#EA2054]'
      : isFinishedNoHits
        ? 'text-[#EA2054]'
        : totalHits > 0
          ? 'text-[#53d337]'
          : 'text-[#8a8a8a]';
  const showHitIcon = isPropBased
    ? showPropTracker
      ? trackerOutcome === 'hit' || trackerOutcome === 'miss'
      : isGraded && (propResult?.showIcon ?? false)
    : hitLabel !== 'Pending';
  const isPositiveHitOutcome = isPropBased
    ? trackerOutcome === 'hit' || (isGraded && (propResult?.won ?? false))
    : isGraded
      ? won
      : totalHits > 0;

  const displayDateLabel = categoryName ? `${categoryName} · ${dateLabel}` : dateLabel;

  const firstName = card.firstName ?? '';
  const lastName = card.lastName ?? '';
  const displayInitials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`;
  const abbreviatedName = formatAbbreviatedName(firstName, lastName, card.displayName);
  const displayImageUrl = resolveMlbStylePlayerImage(card, teamCode, leagueIpEnabled);

  const propParts = getPropTrackerDisplayParts({
    propDisplayLabel,
    propThreshold,
    propOperator,
    sport: card.sport,
  });

  const statusCornerLabel = isLive
    ? 'Live'
    : fixture?.status === 'finished' || isGraded
      ? 'Final'
      : gameStatus;

  const editButton = onEdit ? (
    <button
      type="button"
      onClick={onEdit}
      className="flex items-center gap-[5px] leading-none text-[#53d337] transition-colors hover:text-[#6be04a]"
      style={{
        fontFamily: '"Saira Semi Condensed", sans-serif',
        fontWeight: 700,
        fontSize: '13px',
        letterSpacing: '0.06em',
      }}
      aria-label="Edit selection"
      data-test-id="weekly-selection-edit"
    >
      <Pencil size={13} strokeWidth={2.5} />
      EDIT
    </button>
  ) : null;

  if (isPropBased && showPropTracker) {
    return (
      <article className="mb-3">
        <p
          className="mb-2 pl-2 text-[#8a8a8a]"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {displayDateLabel}
        </p>
        <div className={`relative rounded-[10px] bg-[#1a1a1a] px-4 pb-2 pt-3 ${borderColor}`}>
          <p
            className={`absolute right-4 top-3 uppercase leading-none ${
              isLive ? 'text-[#53d337]' : 'text-[#9a9a9a]'
            }`}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '11px',
              letterSpacing: '0.04em',
            }}
          >
            {statusCornerLabel}
          </p>

          <div className="flex items-start gap-3.5 pr-14">
            <PlayerPickAvatar
              displayImageUrl={displayImageUrl}
              alt={card.displayName}
              initials={displayInitials}
              leagueIpEnabled={leagueIpEnabled}
              teamCode={teamCode}
              sport={card.sport}
              objectFit="contain"
            />

            <div className="min-w-0 flex-1">
              <p
                className="truncate uppercase leading-none text-white"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: '22px',
                  letterSpacing: '0.01em',
                }}
              >
                {abbreviatedName}
              </p>

              <div className="mt-2 flex items-center gap-2">
                {showHitIcon ? <PropOutcomeIcon outcome={trackerOutcome} /> : null}
                {propParts ? (
                  <p
                    className="min-w-0 uppercase leading-none"
                    style={{
                      fontFamily: '"Saira Semi Condensed", sans-serif',
                      fontWeight: 700,
                      fontSize: '11px',
                      letterSpacing: '0.02em',
                    }}
                  >
                    <span className="text-white">{propParts.value}</span>
                    {propParts.label ? (
                      <>
                        {' '}
                        <span className="text-[#BCBCBC]">{propParts.label}</span>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>

              <div className="mt-3">
                <PropStatProgressTracker
                  currentValue={propResultValue}
                  threshold={threshold!}
                  outcome={trackerOutcome}
                />
              </div>

              {editButton ? <div className="mt-3">{editButton}</div> : null}
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="mb-3">
      <p
        className="mb-2 pl-2 text-[#8a8a8a]"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        {displayDateLabel}
      </p>
      <div className={`rounded-[10px] bg-[#1a1a1a] px-4 py-3 ${borderColor}`}>
        <div className="flex items-center">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <PlayerPickAvatar
              displayImageUrl={displayImageUrl}
              alt={card.displayName}
              initials={displayInitials}
              leagueIpEnabled={leagueIpEnabled}
              teamCode={teamCode}
              sport={card.sport}
            />

            <div className="min-w-0 flex-1">
              <p
                className="truncate uppercase leading-tight text-[#ffffff]"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: '18.75px',
                }}
              >
                {firstName.toUpperCase()} {lastName.toUpperCase()}
              </p>
              <p
                className="mt-0.5 text-[#8a8a8a]"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                {isPropBased && propDisplayLabel ? (
                  <>
                    <span className="block text-[#BCBCBC]">{propDisplayLabel}</span>
                    <span className="mt-0.5 block">
                      {card.teamAbbreviation ?? card.teamName}
                      {card.jerseyNumber ? ` • ${card.jerseyNumber}` : ''}
                      {card.position ? ` • ${card.position}` : ''}
                    </span>
                  </>
                ) : (
                  <>
                    {card.teamAbbreviation ?? card.teamName}
                    {card.jerseyNumber ? ` • ${card.jerseyNumber}` : ''}
                    {card.position ? ` • ${card.position}` : ''}
                  </>
                )}
              </p>
              <div className="mt-2 flex items-center gap-2">
                {showHitIcon && (
                  <img
                    src={isPositiveHitOutcome ? '/shapes/correct.svg' : '/shapes/incorrect.svg'}
                    alt={isPositiveHitOutcome ? 'Correct' : 'Incorrect'}
                    className="h-6 w-6 shrink-0"
                  />
                )}
                <span
                  className={`relative -top-[1px] text-[14px] font-bold ${
                    isPpdHit ? '' : 'uppercase'
                  } ${hitColor}`}
                >
                  {hitLabel}
                </span>
                {editButton}
              </div>
            </div>
          </div>

          <div
            className="mx-4 w-px shrink-0 self-stretch"
            style={{ backgroundColor: '#868686' }}
            aria-hidden
          />

          <div className="shrink-0 text-right">
            <p
              className={`mb-2 ${isLive ? 'text-[#53d337]' : 'text-[#8a8a8a]'}`}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '9px',
              }}
            >
              {isLive && (
                <span className="mr-1 inline-block h-[6px] w-[6px] rounded-full bg-[#53d337]" />
              )}
              {gameStatus}
            </p>
            <div className="space-y-1 leading-none">
              {awayTeam && (
                <div className="flex items-center justify-end gap-2">
                  <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
                  <div
                    className="flex min-w-[48px] items-center justify-end gap-4"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                    }}
                  >
                    <span className="uppercase text-[#ffffff]">{awayTeam}</span>
                    <span className="text-[#ffffff]">
                      {fixture?.awayScore != null ? fixture.awayScore : '-'}
                    </span>
                  </div>
                </div>
              )}
              {homeTeam && (
                <div className="flex items-center justify-end gap-2">
                  <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
                  <div
                    className="flex min-w-[48px] items-center justify-end gap-4"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                    }}
                  >
                    <span className="uppercase text-[#ffffff]">{homeTeam}</span>
                    <span className="text-[#ffffff]">
                      {fixture?.homeScore != null ? fixture.homeScore : '-'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
